import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { httpGet, httpGetFile, getAuthorizationHeadersOnly, PAGESIZE } from '../../helpers/http.js';

// course and user are search texts; from and to are dates (yyyy-MM-dd) on the processed date;
// externalPersonId and peCourseId match the whole value.
const toFilterParams = (filters, params = new URLSearchParams()) => {
    Object.entries(filters)
        .filter(([, value]) => value)
        .forEach(([key, value]) => params.set(key, value));
    return params;
};

export const loadAccreditations = createAsyncThunk(
    'accreditation/fetch',
    async (args, { getState }) => {
        const { targetId } = args;
        const { env, auth, accreditation } = getState();
        const { settings } = env;

        const pageIndex = targetId === accreditation.targetId ? accreditation.pageIndex + 1 : 0;

        const params = new URLSearchParams({
            pageIndex: pageIndex,
            pageSize: PAGESIZE,
        });

        const response = await httpGet(`${settings.learningApi}/api/1.0/accreditations/catalogitem/${targetId}?${params}`, auth.user);

        return {
            targetId: targetId,
            list: response.results,
            pageIndex: pageIndex,
            total: response.count,
        };
    },
);

export const loadAccreditationsIfNeeded = createAsyncThunk(
    'accreditation/fetchList',
    async (args, { getState }) => {
        const { accreditation: state, env, auth } = getState();

        const newPage = args.reload ? 0 : state.listPage + 1;
        const filters = { ...state.filters, ...args.filters };

        const params = toFilterParams(filters, new URLSearchParams({
            pageIndex: newPage,
            pageSize: PAGESIZE,
        }));

        const response = await httpGet(`${env.settings.learningApi}/api/1.0/accreditations?${params}`, auth.user);

        return {
            list: response.results,
            page: newPage,
            total: response.count,
            reload: args.reload,
            filters,
        };
    },
    {
        condition: (args, { getState }) => {
            const { accreditation: state } = getState();

            if (!args.reload && (args.index < state.allList.length - 1 || args.index >= state.allTotal - 1)) {
                return false;
            }
        }
    },
);

// All accreditations of the current filters as Excel, built by the API: the list only holds the loaded pages.
export const exportAccreditations = createAsyncThunk(
    'accreditation/export',
    async (args, { getState }) => {
        const { accreditation: state, env, auth } = getState();

        await httpGetFile(
            `${env.settings.learningApi}/api/1.0/accreditations/export?${toFilterParams(state.filters)}`,
            auth.user,
            'accreditaties.xlsx');
    },
);

// Sends an accreditation that PE-online did not accept again. Resolves with what changed (result,
// previous result, data, points, processed date). A refusal (409 alreadyAccepted, 422 noUserRegistration
// or noApplicableMapping) rejects with its reason; anything else rejects without one.
export const retryAccreditation = createAsyncThunk(
    'accreditation/retry',
    async ({ id, targetId }, { getState, rejectWithValue }) => {
        const { env, auth } = getState();
        const params = new URLSearchParams({ accreditationId: id });

        const res = await fetch(
            `${env.settings.learningApi}/api/1.0/accreditations/catalogitem/${targetId}/retry?${params}`,
            { method: 'POST', headers: auth.user ? getAuthorizationHeadersOnly(auth.user) : {} });

        if (!res.ok) {
            const reason = await res.json().then((body) => body?.reason ?? null, () => null);
            return rejectWithValue({ status: res.status, reason });
        }

        return res.json();
    },
);

const accreditationSlice = createSlice({
    name: 'accreditation',
    initialState: {
        status: 'idle',
        targetId: null,
        idString: null,
        list: [],
        pageIndex: -1,
        error: null,
        listStatus: 'idle',
        allList: [],
        allTotal: undefined,
        listPage: -1,
        listRequestId: null,
        filters: {
            course: '', user: '', from: null, to: null, status: '', organization: '', externalPersonId: '', peCourseId: '',
            sort: 'ProcessedNewest',
        },
        exportStatus: 'idle',
        exportError: null,
        // Per accreditation id: { status: 'sending' | 'sent' | 'refused', reason }.
        retries: {},
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadAccreditations.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(loadAccreditations.fulfilled, (state, action) => {
                const { payload } = action;

                state.status = 'success';
                state.targetId = payload.targetId ?? null;
                state.list = payload.pageIndex === 0 ? payload.list : [...state.list, ...payload.list],
                state.pageIndex = payload.pageIndex;
                state.total = payload.total;
            })
            .addCase(loadAccreditations.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(loadAccreditationsIfNeeded.pending, (state, action) => {
                state.listStatus = 'loading';
                if (action.meta.arg.reload) {
                    state.listRequestId = action.meta.requestId;
                }
            })
            .addCase(loadAccreditationsIfNeeded.fulfilled, (state, action) => {
                const { payload } = action;

                // While typing a search text, reloads overtake each other: only the latest
                // counts, and pages loaded for the previous filters are dropped meanwhile.
                const isLatestReload = action.meta.requestId === state.listRequestId;
                if (payload.reload ? !isLatestReload : state.listRequestId !== null) {
                    return;
                }
                state.listRequestId = null;

                state.listStatus = 'success';
                state.allList = payload.reload ? [...payload.list] : [...state.allList, ...payload.list];
                state.listPage = payload.page;
                state.allTotal = payload.total;
                state.filters = payload.filters;
            })
            .addCase(loadAccreditationsIfNeeded.rejected, (state, action) => {
                if (action.meta.requestId === state.listRequestId) {
                    state.listRequestId = null;
                }
                state.listStatus = 'failed';
                state.error = action.error;
            })
            .addCase(exportAccreditations.pending, (state) => {
                state.exportStatus = 'loading';
                state.exportError = null;
            })
            .addCase(exportAccreditations.fulfilled, (state) => {
                state.exportStatus = 'idle';
            })
            .addCase(exportAccreditations.rejected, (state, action) => {
                state.exportStatus = 'failed';
                state.exportError = action.error.message;
            })
            .addCase(retryAccreditation.pending, (state, action) => {
                state.retries[action.meta.arg.id] = { status: 'sending', reason: null };
            })
            .addCase(retryAccreditation.fulfilled, (state, action) => {
                const { id } = action.meta.arg;

                // What the API returns replaces what changed; display name and course name stay.
                state.allList = state.allList.map((item) => item.id === id ? { ...item, ...action.payload } : item);
                state.retries[id] = { status: 'sent', reason: null };
            })
            .addCase(retryAccreditation.rejected, (state, action) => {
                state.retries[action.meta.arg.id] = { status: 'refused', reason: action.payload?.reason ?? null };
            });
    },
});

export default accreditationSlice.reducer;
